(ns app.front.view.public-notice.subscribe
  (:require [app.domain.property.model :as property-model]
            [app.domain.public-notice.model :as public-notice-model]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :as futil]
            [app.front.view.not-found :as not-found]
            [app.front.view.public-notice.confirm-email :as confirm-email]
            [app.front.view.registration.terms-of-service :as tos]
            [app.front.view.workspace.map :as workspace-map]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))


;; -- Subscriptions -----------------------------------------------------------


(rf/reg-sub ::public-notice-view
  :-> :view/public-notice)


(rf/reg-sub ::fetch-status
  :<- [::public-notice-view]
  :-> :fetch-status)




(rf/reg-sub ::committee-item
  :<- [::public-notice-view]
  :-> :committee-item)


(rf/reg-sub ::workspace
  :<- [::public-notice-view]
  :-> :workspace)


(rf/reg-sub ::published?
  :<- [::workspace]
  :-> :workspace/public-notice-published?)


(rf/reg-sub ::permit-type
  :<- [::workspace]
  :-> :workspace/permit-type)


(rf/reg-sub ::permit-number
  :<- [::workspace]
  :-> :workspace/municipal-case-id)


(rf/reg-sub ::address
  :<- [::public-notice-view]
  :-> :address)


(rf/reg-sub ::subdivision
  :<- [::public-notice-view]
  :-> :subdivision)


(rf/reg-sub ::location
  :<- [::public-notice-view]
  :-> :location)


(rf/reg-sub ::feature-enabled?
  :<- [::permit-type]
  :-> public-notice-model/enabled-for?)


(rf/reg-sub ::category
  :<- [::public-notice-view]
  :<- [:language]
  (fn [[view lang] _]
    (-> view
        :category-names
        (i18n-model/get-text-from-entities-or-map lang))))


(rf/reg-sub ::municipality
  :<- [::public-notice-view]
  :<- [:language]
  (fn [[view lang] _]
    (-> view
        :municipality-names
        (i18n-model/get-text-from-entities-or-map lang))))


;; -- Events ------------------------------------------------------------------


(rf/reg-event-fx ::register-contact
  (fn [{:keys [db]} [_ token workspace-id]]
    {:db           (dissoc db ::register-failed?)
     :comm/command {:command         :public-notice/contact-self-registration
                    :data            {:user/token   token
                                      :workspace/id workspace-id}
                    :on-success      [::show-subscribed workspace-id]
                    ;; Token expired (>24h since the confirmation email was sent) or otherwise invalid.
                    ;; Without an explicit :on-unauthorized handler here, the generic response handler
                    ;; falls through to :session/session-expired, which redirects an anonymous visitor
                    ;; to the login page - a dead end that silently drops the subscription attempt.
                    :on-unauthorized [::register-contact.failure]
                    :on-not-found    [::register-contact.failure]
                    :notify          {:success      :public-notice/subscribed
                                      :unauthorized :public-notice/link-expired
                                      :not-found    :public-notice/link-expired}}}))


(rf/reg-event-db ::register-contact.failure
  (fn [db [event status body]]
    (js/console.error event status body)
    (assoc db ::register-failed? true)))


(rf/reg-sub ::register-failed?
  :-> ::register-failed?)


(rf/reg-event-fx ::get-data
  (fn [{:keys [db]} [_ {:keys [workspace-id]}]]
    {:db           (assoc-in db [:view/public-notice :fetch-status] :fetching)
     :comm/command {:command    :public-notice/get-information
                    :data       {:workspace/id workspace-id}
                    :on-success [::data-fetch.success workspace-id]
                    :on-done    [::data-fetch.fail]}}))


(rf/reg-event-fx ::data-fetch.success
  (fn [{:keys [db]} [_ workspace-id _ data]]
    {:db (assoc db :view/public-notice {:workspace          (:workspace data)
                                        :subdivision        (:subdivision data)
                                        :location           (:location data)
                                        :address            (:address data)
                                        :category-names     (:category-names data)
                                        :committee-item     (:committee-item data)
                                        :municipality-names (:municipality-names data)
                                        :workspace-id       workspace-id
                                        :fetch-status       :complete})
     :fx [[:dispatch [::confirm-email/init workspace-id]]]}))


(rf/reg-event-db ::data-fetch.fail
  (fn [db [_ _status _response]]
    (let [error :not-found]
      (update db :view/public-notice assoc :fetch-status error))))


(rf/reg-event-fx ::show-subscribed
  (fn [_ [_ workspace-id]]
    {:route/push {:route  :view/public-notice
                  :params {:workspace-id workspace-id}
                  :query  {:subscribed true}}}))


;; -- View ------------------------------------------------------------------


(defn map-wrapper []
  (let [location   (<sub [::location])
        map-center [(-> location :location.center/x)
                    (-> location :location.center/y)]]
    (when location
      [:div.flex.map-boundary.map-extended.h-80.bg-gray-400.flex.items-center.justify-center
       [workspace-map/workspace-map
        {:center             map-center
         :primaryMapMarkers  [map-center]
         :propertyGeometries {:primaryPropertyGeometry (property-model/geojson-feature
                                                         (-> location :location/shape))}}]])))


(defn id-wrapper [id]
  [:div.flex.rounded-lg>p.p-3.flex id])


(defn info-wrapper [tr workspace-id]
  (let [category       (<sub [::category])
        permit-number  (<sub [::permit-number])
        committee-item (<sub [::committee-item])]
    [:div.p-5.flex.flex-col.justify-left
     [:h3.font-semibold category]
     (when committee-item
       [:div.flex.flex-row.items-start
        [:div.flex.bg-ui-light-gray.pt-5.px-5.mb-5.rounded-lg.flex-col
         [:div.flex
          [:h3.font-semibold.mb-5 (tr [:committee-item/description])]]
         [:div.flex.pb-5
          [ui-rt/editor-legacy {:initial-value committee-item
                                :key           (str "meeting-item-description-" workspace-id)
                                :id            (str "meeting-item-description-" workspace-id)}]]]])
     [:div.flex.flex-col.lg:flex-row.items-center.lg:items-start
      [id-wrapper (str (tr [:workspace/municipal-case-id]) ": " permit-number)]
      [:div.fel.rounded-lg.bg-ui-light-gray.mx-5.lg:mx-0.w-48.lg:w-1.h-1.lg:h-10]
      [id-wrapper (str (tr [:workspace/id]) ": " workspace-id)]]]))


(defn sub-wrapper [tr workspace-id subscribed?]
  (let [logged-in?       (<sub [:session/logged-in?])
        register-failed? (<sub [::register-failed?])]
    [:div.flex.p-5.rounded-lg.flex-col
     [:h3.font-semibold.mb-5
      (if (not subscribed?)
        (tr [:public-notice/subscribe-title])
        (tr [:public-notice/subscribed-title]))]
     [:p (if (not subscribed?)
           (if logged-in?
             (tr [:public-notice/subscribe-logged-in])
             (tr [:public-notice/subscribe]))
           (tr [:public-notice/subscribed]))]
     (when (and (not subscribed?) register-failed?)
       [:p.text-ui-red-140 (tr [:public-notice/link-expired])])
     (when (not subscribed?) [confirm-email/view workspace-id])]))


(defn public-notice-view [workspace-id subscribed?]
  (let [tr           (<sub [:tr])
        address      (or (<sub [::address])
                         (tr [:address/address-missing]))
        municipality (<sub [::municipality])
        subdivision  (<sub [::subdivision])
        full-address (str address ", " municipality ", " (tr [(keyword "subdivision" subdivision)]))]
    [:div.flex.flex-col.justify-center
     [:h1.flex.m-auto.lg:m-0.justify-start.font-bold.w-3-4 full-address]
     [:div.flex.flex-col.lg:flex-row.items-start
      [:section.m-5.mx-auto.w-3-4.rounded-lg
       [:div.flex.justify-center.flex-col
        [map-wrapper]
        [info-wrapper tr workspace-id]]]
      [:section.mx-auto.lg:mx-5.lg:mt-5.w-3-4.lg:w-1-4.rounded-lg.flex.flex-col
       [sub-wrapper tr workspace-id subscribed?]]]]))


(defn public-notice [match]
  (let [workspace-id (-> match :parameters :path :workspace-id)
        subscribed?  (true? (-> match :parameters :query :subscribed))
        fetch-status (<sub [::fetch-status])
        fetch-done?  (= fetch-status :complete)
        not-found?   (= fetch-status :not-found)
        enabled?     (<sub [::feature-enabled?])
        published?   (<sub [::published?])]
    [:div.my-5.mx-auto.max-w-6xl
     [tos/doc-modal match]
     (cond
       (and fetch-done? enabled? published?)
       [public-notice-view workspace-id subscribed?]
       (or not-found? (and fetch-done? (or (not enabled?) (not published?))))
       [not-found/not-found-view]
       :else
       (cc/loading-spinner))]))


(def route
  {:name         :view/public-notice
   :view         public-notice
   :own-layout?  true
   :hide-footer? true
   :public?      true
   :parameters   {:path  {:workspace-id s/Str}
                  :query (st/merge
                           tos/route-parameters
                           (st/optional-keys {:register   s/Str
                                              :subscribed s/Bool}))}
   :controllers  (concat
                   tos/doc-modal-controller
                   [{:parameters {:path  [:workspace-id]
                                  :query [:register :subscribed]}
                     :start      (fn [params]
                                   (let [workspace-id (-> params :path :workspace-id)]
                                     (when (some? (-> params :query :register))
                                       (>evt [::register-contact (-> params :query :register) workspace-id]))
                                     (futil/set-title (str "Public Notice " workspace-id))
                                     (>evt [::get-data (:path params)])))}])})
